import {
  ConfirmableActionFragment,
  Input_Patientportal_ActionConfirmationMsg,
  Patientportal_ConfirmationCodeType,
  Patientportal_ConfirmationProviderEmailMsg,
  Patientportal_ConfirmationProviderMsg,
  Patientportal_ConfirmationProviderType,
  Patientportal_ConfirmationTokenMode,
  VerifyThirdPartyCommunicationMutation,
} from '@/generated/graphql';
import { FetchResult, MaybeMasked } from '@apollo/client';
import { ActionType } from '@components/confirmable-action/types';
import { Button, Input, Modal, Result, Space } from 'antd';
import Title from 'antd/lib/typography/Title';
import Image from 'next/image';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import PinInput from 'react-pin-input';
import { buildTimestampDateTime } from '../../helpers/formatters';
import { storeConfirmableAction } from '../../helpers/stored-confirmable-action';
import { ColorStyle } from '../../styles/colors';
import { AveliosButton } from '../common-layout/avelios-button';
import styles from './confirmable-action.module.scss';

interface ConfirmableActionModalProps<T> {
  confirmableAction: ConfirmableActionFragment | undefined;
  confirmAction: (
    actionConfirmation: Input_Patientportal_ActionConfirmationMsg,
  ) => Promise<FetchResult<MaybeMasked<VerifyThirdPartyCommunicationMutation>>>;
  actionType: ActionType;
  resendAction?: () => void;
  confirmActionLoading: boolean;
  onDone: () => void;
  onCancel: () => void;
  email: string | null | undefined;
  required?: boolean;
}

export const ConfirmableActionModal = <T,>({
  confirmableAction,
  confirmAction,
  actionType,
  resendAction,
  confirmActionLoading,
  onDone,
  onCancel,
  email,
  required = false,
}: ConfirmableActionModalProps<T>): ReactElement<any, any> | null => {
  const { formatMessage } = useIntl();

  const [confirmationCode, setConfirmationCode] = useState<string>();
  const [timer, setTimer] = useState<number | null>(null);
  const [invalidCode, setInvalidCode] = useState<boolean>(false);
  let pinInputRef: PinInput | null = null;

  const twoFactorAuthentication = actionType === ActionType.TwoFactorAuthentication;

  // for now always use the email provider
  const emailConfirmationProvider:
    | [Patientportal_ConfirmationProviderMsg, Patientportal_ConfirmationProviderEmailMsg]
    | undefined = useMemo(() => {
    if (confirmableAction === undefined) {
      return undefined;
    }

    const emailProviderWrapper: Patientportal_ConfirmationProviderMsg | undefined = (
      confirmableAction.confirmationProviders ?? []
    )
      .filterPresent()
      .find(
        (provider) =>
          provider?.type ===
          Patientportal_ConfirmationProviderType.ConfirmationProviderProviderTypeEmail,
      );
    if (emailProviderWrapper === undefined) {
      return undefined; // TODO: internal error for now, since only email provider is implemented in the backend
    }

    const emailProvider = emailProviderWrapper.email;
    if (emailProvider === null || emailProvider === undefined) {
      return undefined;
    }

    return [emailProviderWrapper, emailProvider];
  }, [confirmableAction]);

  useEffect(() => {
    if (confirmableAction !== undefined) {
      storeConfirmableAction(confirmableAction);
    }
  }, [confirmableAction]);

  const emailConfirmationCodeInfo = emailConfirmationProvider?.[1]?.confirmationCodeInfo;
  const emailConfirmationTokenMode = emailConfirmationProvider?.[1]?.confirmationTokenMode;

  const desiredConfirmationCodeLength = emailConfirmationCodeInfo?.length ?? 6;

  const clearStates = () => {
    setConfirmationCode('');
    pinInputRef?.clear();
    setInvalidCode(false);
  };

  const handleResend = () => {
    setTimer(60);
    clearStates();
    resendAction?.();
  };

  const onChange = (value: string) => {
    const updatedValue =
      emailConfirmationCodeInfo?.type ===
      Patientportal_ConfirmationCodeType.ConfirmationCodeTypeAlphaNumericUppercase
        ? value.toUpperCase()
        : value;
    setConfirmationCode(updatedValue);
    if (invalidCode) {
      setInvalidCode(false);
    }
  };

  const onComplete = (value: string) => {
    if (value?.length === desiredConfirmationCodeLength) {
      confirmAction({
        confirmableActionId: confirmableAction?.id,
        actionToken: confirmableAction?.actionToken,
        confirmationCode: value,
        confirmationProviderIndex: 0,
      })?.then(({ errors }) => {
        if (errors?.length) {
          setInvalidCode(true);
        }
      });
    }
  };

  const onClose = () => {
    onCancel();
    clearStates();
    setTimer(null);
    onDone();
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!timer || timer <= 0) {
        clearInterval(intervalId);
      } else {
        setTimer((current) => (current || 1) - 1);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timer]);

  return (
    <Modal
      open={emailConfirmationProvider !== undefined}
      footer={null}
      closable={!required}
      onCancel={required ? undefined : onClose}
      style={{ maxWidth: '90vw' }}
    >
      <div className={styles.twoFactorWrapper}>
        <Title level={2}>
          {formatMessage({
            id: twoFactorAuthentication ? 'email-confirmation' : 'account-verification',
          })}
        </Title>
        {twoFactorAuthentication && <p>{formatMessage({ id: 'email-confirmation.two-factor' })}</p>}
        <div className={styles.twoFactorImageWrapper}>
          <Image src={'/two-factor-auth.jpg'} objectFit={'contain'} layout={'fill'} />
        </div>
        {emailConfirmationCodeInfo !== null && emailConfirmationCodeInfo !== undefined ? (
          <>
            <p className={styles.description}>
              {formatMessage(
                {
                  id: twoFactorAuthentication
                    ? 'email-confirmation.description'
                    : 'account-verification.description',
                },
                { email: email },
              )}
            </p>

            <div>
              <div style={{ color: invalidCode ? ColorStyle.danger : undefined, minHeight: 25 }}>
                {invalidCode ? formatMessage({ id: 'account-verification.invalid-code' }) : ''}
              </div>

              {desiredConfirmationCodeLength <= 8 ? (
                <PinInput
                  initialValue={confirmationCode}
                  length={desiredConfirmationCodeLength}
                  onComplete={onComplete}
                  onChange={onChange}
                  disabled={confirmActionLoading}
                  type={
                    emailConfirmationCodeInfo.type ===
                    Patientportal_ConfirmationCodeType.ConfirmationCodeTypeNumeric
                      ? 'numeric'
                      : 'custom'
                  }
                  inputMode={
                    emailConfirmationCodeInfo.type ===
                    Patientportal_ConfirmationCodeType.ConfirmationCodeTypeNumeric
                      ? 'number'
                      : 'text'
                  }
                  style={{ padding: '10px' }}
                  inputStyle={{
                    borderColor: invalidCode ? ColorStyle.danger : ColorStyle.default,
                    borderRadius: 12,
                    height: 40,
                    width: 40,
                  }}
                  inputFocusStyle={{
                    borderColor: invalidCode ? ColorStyle.danger : ColorStyle.focus,
                  }}
                  autoSelect={true}
                  regexCriteria={/^[A-Za-z0-9]*$/}
                  ref={(n) => (pinInputRef = n)}
                />
              ) : (
                <Input
                  value={confirmationCode}
                  onChange={(e) => {
                    const value = e.target.value;
                    onChange(value);
                    onComplete(value);
                  }}
                  maxLength={desiredConfirmationCodeLength}
                  showCount
                  disabled={confirmActionLoading}
                  type={
                    emailConfirmationCodeInfo.type ===
                    Patientportal_ConfirmationCodeType.ConfirmationCodeTypeAlphaNumeric
                      ? 'number'
                      : 'text'
                  }
                  style={{
                    borderColor: invalidCode ? ColorStyle.danger : ColorStyle.default,
                    marginBottom: 10,
                  }}
                />
              )}
            </div>

            <div>
              <Space>
                <span>{formatMessage({ id: 'account-verification.valid-until' })}</span>
                {buildTimestampDateTime(confirmableAction?.expiresAt)}
              </Space>
            </div>

            <Space>
              <span>{formatMessage({ id: 'account-verification.no-email' })}</span>
              <AveliosButton
                onClick={() => handleResend()}
                disabled={!!timer}
                type={'link'}
                style={{ padding: 0 }}
                loading={confirmActionLoading}
              >
                {formatMessage({ id: 'action.send-again' })}
              </AveliosButton>
              {!!timer && <span style={{ color: 'grey' }}>{timer + 's'}</span>}
            </Space>
            {!required && (
              <AveliosButton
                style={{ marginTop: 22 }}
                block
                loading={confirmActionLoading}
                size={'large'}
                type={'link'}
                onClick={onClose}
              >
                {formatMessage({ id: 'confirmable-action.skip' })}
              </AveliosButton>
            )}
          </>
        ) : emailConfirmationTokenMode ===
          Patientportal_ConfirmationTokenMode.ConfirmationTokenModeFullConfirm ? (
          <Result
            status="info"
            title={formatMessage({
              id: 'confirmable-action.action-token-sent.email.title',
            })}
            subTitle={formatMessage({
              id: 'confirmable-action.action-token-sent.email-full.sub-title',
            })}
            extra={
              <Button onClick={onDone}>
                {formatMessage({
                  id: 'confirmable-action.action-token-sent.email-full.done',
                })}
              </Button>
            }
          />
        ) : emailConfirmationTokenMode ===
          Patientportal_ConfirmationTokenMode.ConfirmationTokenModePartialConfirm ? (
          <Result
            status="info"
            title={formatMessage({
              id: 'confirmable-action.action-token-sent.email.title',
            })}
            subTitle={formatMessage({
              id: 'confirmable-action.action-token-sent.email-partial.sub-title',
            })}
            extra={
              <Button
                onClick={() => {
                  // already partially confirmed so we only need action-token
                  confirmAction({
                    confirmableActionId: confirmableAction?.id,
                    actionToken: confirmableAction?.actionToken,
                    confirmationProviderIndex: 0,
                  });
                }}
              >
                {formatMessage({
                  id: 'confirmable-action.action-token-sent.email-partial.done',
                })}
              </Button>
            }
          />
        ) : (
          // TODO: handle partial-confirm token mode
          <Result
            status="error"
            title={formatMessage({ id: 'error.title' })}
            subTitle={formatMessage({ id: 'error.message' })}
          />
        )}
      </div>
    </Modal>
  );
};
